import { ApolloProvider } from '@apollo/client/react';
import { RouterProvider } from 'react-router-dom';
import { ConfigProvider, getAntdConfig } from '@/lib/antd-config';
import { App as AntdApp } from 'antd';
import { ConfirmModalBridge } from '@/utils/confirmAction';
import { apolloClient } from '@/lib/apollo-client';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthProvider } from '@/contexts/AuthContext';
import { ImpersonationProvider } from '@/contexts/ImpersonationContext';
import { ThemeProvider, useTheme } from '@/contexts/ThemeContext';
import { router } from '@/routes';
import './App.css';
import './styles/common.css';

// Expose test utilities in development mode (use console.debug so real errors stay visible)
if (import.meta.env.DEV) {
  import('@/utils/test-auth').then(({ testAuth }) => {
    (window as any).testAuth = testAuth;
    console.debug(
      '%c[Test Auth] Test utilities available!',
      'color: #4CAF50; font-weight: bold; font-size: 14px;'
    );
    console.debug(
      '%cAvailable commands:\n' +
        '  window.testAuth.expireAccessToken() - Expire access token\n' +
        '  window.testAuth.expireRefreshToken() - Expire refresh token\n' +
        '  window.testAuth.expireAllTokens() - Expire both tokens\n' +
        '  window.testAuth.setTokenExpiration(seconds) - Set token expiration\n' +
        '  window.testAuth.checkTokenStatus() - Check token status\n' +
        '  window.testAuth.clearAuth() - Clear all auth data',
      'color: #2196F3; font-size: 12px;'
    );
  });
}

// Inner component that has access to ThemeContext
function AppContent() {
  const { themeMode } = useTheme();
  const antdConfig = getAntdConfig(themeMode === 'dark');

  return (
    <ConfigProvider theme={antdConfig}>
      <AntdApp>
        <ConfirmModalBridge />
        <RouterProvider router={router} />
      </AntdApp>
    </ConfigProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ApolloProvider client={apolloClient}>
        <ThemeProvider>
          <AuthProvider>
            <ImpersonationProvider>
              <AppContent />
            </ImpersonationProvider>
          </AuthProvider>
        </ThemeProvider>
      </ApolloProvider>
    </ErrorBoundary>
  );
}

export default App;

